"use client"

import * as React from "react"

import {
  clearDevChunkReloadFlag,
  isChunkLoadError,
  reloadOnStaleDevChunk,
} from "@/lib/chunk-load-error"

/**
 * Auto-reload when a stale lazy chunk fails (dev HMR or post-deploy cache skew).
 * Prevents a shell-only blank page before the route error boundary mounts.
 *
 * A successful mount clears the flag so a later failure can recover again.
 * Reload is cooled down so a persistently bad import cannot loop.
 */
export function DevChunkLoadRecovery() {
  React.useEffect(() => {
    clearDevChunkReloadFlag()

    function maybeReload(error: unknown) {
      if (!isChunkLoadError(error)) return
      reloadOnStaleDevChunk()
    }

    const onError = (event: ErrorEvent) => {
      maybeReload(event.error ?? event.message)
    }
    const onRejection = (event: PromiseRejectionEvent) => {
      maybeReload(event.reason)
    }

    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onRejection)
    return () => {
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onRejection)
    }
  }, [])

  return null
}
